import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const DataDeletion = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 relative flex items-center justify-center">
          <Button variant="ghost" size="icon" className="shrink-0 absolute left-4" asChild>
            <Link to="/">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img
              src="/dibs_assets/red_logo_with_transparent_bg.png"
              alt="Dibs"
              className="h-7 object-contain"
            />
            <span className="text-primary font-sans font-bold text-xl tracking-tight">Dibs</span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight mb-2">Data Deletion</h1>
        <p className="text-sm text-muted-foreground mb-12">Last updated: May 27, 2026</p>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-10 text-[15px] leading-relaxed">

          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">1. Your Right to Deletion</h2>
            <p className="text-muted-foreground">
              You have the right to request deletion of the personal data <strong className="text-foreground">Dibs</strong> holds about you. This applies to both:
            </p>
            <ul className="text-muted-foreground mt-3 ml-4 list-disc space-y-1">
              <li><strong className="text-foreground">Business Users</strong> — business owners and staff with a Dibs account.</li>
              <li><strong className="text-foreground">End-User Customers</strong> — individuals who have interacted with a Dibs-powered booking page or chat channel (WhatsApp, Telegram, Instagram, email).</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              This right is granted under Singapore's Personal Data Protection Act 2012 (PDPA), the EU General Data Protection Regulation (GDPR), and other applicable data-protection laws.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">2. How to Request Deletion</h2>

            <h3 className="text-lg font-medium mt-6 mb-3">Option A — Email Us</h3>
            <p className="text-muted-foreground">
              Send an email to <a href="mailto:support@usedibs.com" className="text-primary underline">support@usedibs.com</a> from the email address associated with your account or booking, with the subject line <em>"Data Deletion Request"</em>.
            </p>
            <p className="text-muted-foreground mt-3">
              Please include:
            </p>
            <ul className="text-muted-foreground mt-2 ml-4 list-disc space-y-1">
              <li>Your full name (and business name, if you are a Business User)</li>
              <li>The email address, phone number, or social handle you used to interact with Dibs</li>
              <li>A brief statement that you wish to have your personal data deleted</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">Option B — In-App (Business Users)</h3>
            <p className="text-muted-foreground">
              If you have a Dibs business account, you can request deletion from inside the admin dashboard: <strong className="text-foreground">Settings → Account → Request Account Deletion</strong>. This sends a confirmation email and queues your account, contacts, conversations, and AI memory for deletion.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">Option C — Through a Business that Uses Dibs</h3>
            <p className="text-muted-foreground">
              If you are an end-customer who interacted with a Dibs-powered booking page or chat, you can also ask the business directly to delete your contact. Business owners can tombstone individual contacts from their Dibs dashboard, which removes you from their AI memory, segments, and campaigns.
            </p>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">3. What Gets Deleted</h2>
            <p className="text-muted-foreground">When we process your deletion request, we remove:</p>
            <ul className="text-muted-foreground mt-3 ml-4 list-disc space-y-1">
              <li>Your account profile and login credentials (Business Users)</li>
              <li>Contact records and customer-graph profile (name, phone, email, social identifiers)</li>
              <li>Chat history across all connected channels (WhatsApp, Telegram, Instagram, email)</li>
              <li>AI conversation memory and personalized context</li>
              <li>Booking history (unless legally required to retain — see Section 4)</li>
              <li>Tokens and access credentials linking us to your Meta, Telegram, or other third-party accounts</li>
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">4. What We Retain (and Why)</h2>
            <p className="text-muted-foreground">A small amount of data may be retained after a deletion request, for legal or operational reasons:</p>
            <ul className="text-muted-foreground mt-3 ml-4 list-disc space-y-1">
              <li><strong className="text-foreground">Billing &amp; tax records</strong> — retained for up to 7 years to comply with Singapore IRAS requirements and equivalent tax laws.</li>
              <li><strong className="text-foreground">Anonymized analytics</strong> — aggregated, non-identifying usage data may be kept indefinitely.</li>
              <li><strong className="text-foreground">Records required by law enforcement</strong> — where we have received a valid legal request to preserve data.</li>
              <li><strong className="text-foreground">Tombstone markers</strong> — we keep a record that a contact was deleted (without the personal data itself), so we don't accidentally re-create the contact from a future inbound message.</li>
            </ul>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">5. How Long It Takes</h2>
            <p className="text-muted-foreground">
              We process deletion requests within <strong className="text-foreground">30 days</strong> of receiving them, in line with GDPR and PDPA requirements. In most cases the deletion is completed within a few business days. We will email you a confirmation once your request has been processed.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">6. Disconnecting Dibs from Facebook / Meta</h2>
            <p className="text-muted-foreground">
              If you connected Dibs to your WhatsApp Business Account through Meta, you can revoke that connection at any time:
            </p>
            <ul className="text-muted-foreground mt-3 ml-4 list-disc space-y-1">
              <li><strong className="text-foreground">In Dibs:</strong> Admin dashboard → Connections → WhatsApp → Disconnect.</li>
              <li><strong className="text-foreground">In Meta:</strong> facebook.com → Settings → Business Integrations → find <em>UseDibs</em> → Remove.</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              Disconnecting revokes our access tokens immediately. To also delete the associated data we already stored, follow the deletion process in Section 2.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold mb-4">7. Questions?</h2>
            <p className="text-muted-foreground">
              If you have any questions about this process, or need help with a deletion request, contact us at <a href="mailto:support@usedibs.com" className="text-primary underline">support@usedibs.com</a>. For the full picture of how we handle personal data, see our <Link to="/privacy-policy" className="text-primary underline">Privacy Policy</Link>.
            </p>
          </section>
        </div>
      </article>
    </div>
  );
};

export default DataDeletion;
